import { config } from '@uhg-abyss/web/tools/config';
import { http } from '@uhg-abyss/web/tools/http';
import { event as eapmEvent } from '@uhg-abyss/web/tools/event';
import {
  getTinStatusError,
  uploadDocumentError,
  submitTinError,
  saveAsDraftError,
  npiValidationError,
  updateSubmissionError,
} from './errors';

export const getTinStatus = async (payload) => {
  const { action } = payload;
  const { billingTin, requestId, test } = action.payload;

  const response = await http({
    id: 'epv-tin-status',
    method: 'GET',
    cache: false,
    withCredentials: true,
    url: `${config('LINK_EPV_API_URL')}/tin/validate/v1`,
    handleError: getTinStatusError,
    query: {
      billingTin,
      requestId,
      test,
    },
  });
  const { duration, endpoint, status, data } = response;
  const successData = {
    endpoint,
    duration,
    status,
  };
  eapmEvent('EAPM_TRANSACTION_SUCCESS', successData);

  return data;
};

export const uploadDocument = async (request) => {
  const { payload } = request.action;
  const maxRetries = config('UPLOAD_DOC_RETRIES');
  let retryCount = 0;

  const response = await http({
    id: 'epv-document-upload',
    method: 'POST',
    withCredentials: true,
    cache: false,
    url: `${config('LINK_EPV_API_URL')}/tin/document/v1`,
    handleError: (res) => {
      if (
        retryCount < maxRetries &&
        config('DOC360_RETRY_FAULT_CODES').includes(res.data.faultCode)
      ) {
        return false;
      }
      return uploadDocumentError(res);
    },
    retry: {
      maxRetries,
      shouldRetry: (res) => {
        if (config('DOC360_RETRY_FAULT_CODES').includes(res.data.faultCode)) {
          retryCount += 1;
          return true;
        }
        return false;
      },
    },

    formData: {
      billingTin: payload.billingTin,
      requestId: payload.requestId,
      fileType: payload.fileType,
      trackingNumber: payload.trackingNumber,
      file: payload.file,
      test: payload.test,
    },
  });
  const { duration, endpoint, status, data } = response;
  const successData = {
    endpoint,
    duration,
    status,
  };
  eapmEvent('EAPM_TRANSACTION_SUCCESS', successData);

  return data;
};

export const uploadSignatureRequest = async (request) => {
  const { payload } = request.action;
  const maxRetries = config('UPLOAD_DOC_RETRIES');
  let retryCount = 0;
  const response = await http({
    id: 'epv-signature-upload',
    method: 'POST',
    withCredentials: true,
    cache: false,
    url: `${config('LINK_EPV_API_URL')}/tin/document/v2`,
    handleError: (res) => {
      if (
        retryCount < maxRetries &&
        config('DOC360_RETRY_FAULT_CODES').includes(res.data.faultCode)
      ) {
        return false;
      }
      return uploadDocumentError;
    },
    retry: {
      maxRetries,
      shouldRetry: (res) => {
        if (config('DOC360_RETRY_FAULT_CODES').includes(res.data.faultCode)) {
          retryCount += 1;
          return true;
        }
        return false;
      },
    },

    formData: {
      billingTin: payload.billingTin,
      requestId: payload.requestId,
      fileType: payload.fileType,
      trackingNumber: payload.trackingNumber,
      file: payload.file,
      test: payload.test,
    },
  });
  const { duration, endpoint, status, data } = response;
  const successData = {
    endpoint,
    duration,
    status,
  };
  eapmEvent('EAPM_TRANSACTION_SUCCESS', successData);
  return data;
};

export const updateSubmission = async (payload) => {
  const { action } = payload;

  const response = await http({
    id: 'epv-update-submission',
    method: 'POST',
    cache: false,
    withCredentials: true,
    url: `${config('LINK_EPV_API_URL')}/tin-update/v1`,
    handleError: updateSubmissionError,
    body: action.payload,
  });
  const { duration, endpoint, status, data } = response;
  eapmEvent('EAPM_TRANSACTION_SUCCESS', { endpoint, duration, status });
  return data;
};

export const tinSubmission = async (payload) => {
  const { action } = payload;

  const response = await http({
    id: 'epv-tin-submission',
    method: 'POST',
    cache: false,
    withCredentials: true,
    url: `${config('LINK_EPV_API_URL')}/tin/v1`,
    handleError: submitTinError,
    body: action.payload,
  });
  const { duration, endpoint, status, data } = response;
  const successData = {
    endpoint,
    duration,
    status,
  };
  eapmEvent('EAPM_TRANSACTION_SUCCESS', successData);

  return data;
};

export const callSaveAsDraft = async (payload) => {
  const { action } = payload;

  const response = await http({
    id: 'epv-tin-savedraft',
    method: 'POST',
    cache: false,
    withCredentials: true,
    url: `${config('LINK_EPV_API_URL')}/user/draft/v1`,
    handleError: saveAsDraftError,
    body: action.payload,
  });
  const { duration, endpoint, status, data } = response;
  const successData = {
    endpoint,
    duration,
    status,
  };
  eapmEvent('EAPM_TRANSACTION_SUCCESS', successData);
  return data;
};

export const getDraft = async () => {
  const response = await http({
    id: 'epv-tin-savedraft',
    method: 'GET',
    cache: false,
    withCredentials: true,
    url: `${config('LINK_EPV_API_URL')}/user/draft/v1`,
  });
  const { duration, endpoint, status, data } = response;
  const successData = {
    endpoint,
    duration,
    status,
  };
  eapmEvent('EAPM_TRANSACTION_SUCCESS', successData);
  return data;
};
export const deleteDraft = async (payload) => {
  const { action } = payload;
  const { billingTin, index } = action.payload;

  const response = await http({
    id: 'epv-tin-deletedraft',
    method: 'DELETE',
    cache: false,
    withCredentials: true,
    url: `${config('LINK_EPV_API_URL')}/user/draft/v1`,
    query: {
      billingTin,
    },
  });
  const { duration, endpoint, status, data } = response;
  const successData = {
    endpoint,
    duration,
    status,
  };
  eapmEvent('EAPM_TRANSACTION_SUCCESS', successData);
  return { data, index };
};

export const npiValidations = async (payload) => {
  const { action } = payload;
  const { isProvider, npi, trackingNumber } = action.payload;
  const response = await http({
    id: 'epv-tin-npiValidations',
    method: 'GET',
    cache: false,
    withCredentials: true,
    url: `${config('LINK_EPV_API_URL')}/npi/v1`,
    handleError: npiValidationError,
    query: {
      isProvider,
      npi,
      trackingNumber,
    },
  });
  const { duration, endpoint, status, data } = response;
  const successData = {
    endpoint,
    duration,
    status,
  };
  eapmEvent('EAPM_TRANSACTION_SUCCESS', successData);

  return data;
};
